import {createStyles, Switch} from '@mantine/core';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepTypeMenu from 'pipelines/components/StepTypeMenu';
import {stepTypeIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import {getLatestStepVersion, getOutputStepDatas, getSourceStepTypes} from 'pipelines/StepConnectionUtils';
import {MenuPanelSection} from 'ui-library/menus/MenuPanel';
import PlainSearchInput, {BorderRadius, ClearType} from 'ui-library/PlainSearchInput';

/** Step types that are dangerous to add, even though they are supported by the step config editor. */
// Keep in sync with _NON_ADDABLE_STEP_TYPES in benchling/pipelines/api/pipelines.py
const NON_ADDABLE_STEP_TYPES: Partial<Record<PipelineStepType, boolean>> = {
  // BULK_IMPORT is an internal step for code-defined pipelines and should not be user-addable
  [PipelineStepType.BULK_IMPORT]: true,
  // While CHART_VIEW is supported by the config editor, only certain charts types are supported.
  [PipelineStepType.CHART_VIEW]: true,
  // CONNECTION_DATA_DATASET is gated behind the connection dataset creation flag
  [PipelineStepType.CONNECTION_DATA_DATASET]:
    !BenchlingConfig.ENABLE_ANALYSIS_TOOL_CONNECTION_DATASET_CREATION,
  // CREATE_METERING_USAGE_CHART has a step config UI, but is for use in internal admin
  [PipelineStepType.CREATE_METERING_USAGE_CHART]: true,
  // CREATE_PROCEDURE_DATASET is currently hidden until we have improved the UX for configuring data from
  // studies/worksheets/procedure runs. This prevents users from adding on adhoc, but procedure dataset steps
  // continue to be generated from our analysis button entrypoints (Studies, Worksheets, Procedure Runs).
  [PipelineStepType.CREATE_PROCEDURE_DATASET]: true,
  // PARAMETER_OPTIMIZATION_CONFIGURATION steps cannot be removed once added.
  [PipelineStepType.NDC_COMBINE_DOMAINS_TO_VARIABLE_PAIRS]: true,
  [PipelineStepType.NDC_EXPAND_VARIABLE_PAIRS_TO_DOMAINS]: true,
  [PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION]: true,
  // PLATE_SCHEMA_DATASET is gated behind the experimental schemas flag
  [PipelineStepType.PLATE_SCHEMA_DATASET]: !BenchlingConfig.DOMAIN_GRAPH_ENABLE_EXPERIMENTAL_SCHEMAS,
  // RUN_HIGHRES_PROTOCOL steps are an experimental feature
  [PipelineStepType.RUN_HIGHRES_PROTOCOL]:
    !BenchlingConfig.ENABLE_ANALYSIS_TOOL_INTERACTIVE_FLOWCHART_EXPERIMENTAL,
  [PipelineStepType.EXTRACT_DATA_FRAME_VALUE]:
    !BenchlingConfig.ENABLE_ANALYSIS_TOOL_EXTRACT_DATA_FRAME_VALUE_STEP,
  // TODO(BNCH-145917): SEND_USER_NOTIFICATION is still in development
  [PipelineStepType.SEND_USER_NOTIFICATION]: true,
  // WAREHOUSE_SQL_DATASET is gated behind the ANALYSIS_DIRECT_WAREHOUSE_SQL flag
  [PipelineStepType.WAREHOUSE_SQL_DATASET]: !BenchlingConfig.ANALYSIS_DIRECT_WAREHOUSE_SQL,
  [PipelineStepType.APPLY_COLUMN_MAPPING]: !BenchlingConfig.ENABLE_INSIGHTS_LABS,
};

/**
 * True if the step type is safe to add and edit in the generic way.
 */
export function canAddStepType(stepType: PipelineStepType): boolean {
  // Don't allow adding steps which can't be configured in the generic way.
  return (
    stepTypeIsConfigEditorSupported(stepType, getLatestStepVersion(stepType)) &&
    !NON_ADDABLE_STEP_TYPES[stepType]
  );
}

/**
 * Filter function for source step types that can be added.
 */
export function filterSourceStepTypes(stepType: PipelineStepType): boolean {
  const sourceStepTypes = getSourceStepTypes();
  return canAddStepType(stepType) && sourceStepTypes.includes(stepType);
}

/** Step types that cannot be used as replacements for a table source. */
const NON_REPLACEABLE_STEP_TYPES: Partial<Record<PipelineStepType, boolean>> = {
  // Template variables are not a data source — they cannot replace a table.
  [PipelineStepType.DEFINE_TEMPLATE_VARIABLES]: true,
};

/**
 * Filter function for source step types that can replace a table source.
 * Excludes file-producing step types (since the output type must match) and
 * step types that are not meaningful as table replacements.
 */
export function filterReplaceableSourceStepTypes(stepType: PipelineStepType): boolean {
  if (!filterSourceStepTypes(stepType)) {
    return false;
  }
  if (NON_REPLACEABLE_STEP_TYPES[stepType]) {
    return false;
  }
  // Only allow step types that produce dataset (table) output, not file output.
  const outputDatas = getOutputStepDatas(stepType);
  return outputDatas.length > 0 && outputDatas.every((o) => o.kind === PipelineStepDataKind.DATASET);
}

const useStyles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  searchBar: {
    flexShrink: 0,
    paddingBottom: '12px',
  },
  searchInput: {
    width: '100%',
  },
  menuWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  footer: {
    flexShrink: 0,
    padding: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

interface StepTypeSelectorProps {
  selectedStepType: PipelineStepType | null;
  onSelectStepType: (stepType: PipelineStepType | null) => void;
  extraSections?: Array<MenuPanelSection>;
  /**
   * Optional override for the step type filter. If not provided, defaults to `filterSourceStepTypes`
   * which filters to source step types that can be added via the config editor.
   */
  filterStepTypes?: (stepType: PipelineStepType) => boolean;
}

/**
 * Shared step type selection UI used by both AddStepRightSidebar and ReplaceSourceStepRightSidebar.
 *
 * Renders a search bar, StepTypeMenu with source step types, and a descriptions toggle.
 */
export default function StepTypeSelector({
  selectedStepType,
  onSelectStepType,
  extraSections,
  filterStepTypes,
}: StepTypeSelectorProps): JSX.Element {
  const {classes} = useStyles();
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div className={classes.container}>
      <div className={classes.searchBar}>
        <PlainSearchInput
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          onEscape={() => {
            setSearchQuery('');
          }}
          clearType={ClearType.X}
          borderRadius={BorderRadius.LARGE}
          className={classes.searchInput}
        />
      </div>
      <div className={classes.menuWrapper}>
        <StepTypeMenu
          filterStepTypes={filterStepTypes ?? filterSourceStepTypes}
          selectedStepType={selectedStepType}
          onSelectStepType={onSelectStepType}
          showDescription={showDescriptions}
          searchQuery={searchQuery}
          extraSections={extraSections}
        />
        <div className={classes.footer}>
          <Switch
            label="Step descriptions"
            checked={showDescriptions}
            onChange={(e) => {
              setShowDescriptions(e.currentTarget.checked);
            }}
            styles={{
              root: {justifyContent: 'flex-end'},
              body: {alignItems: 'center'},
            }}
          />
        </div>
      </div>
    </div>
  );
}
